import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { billingApi, formatCents, type BillingStatus } from '@/services/billingApi';
import { RecruiterPanel } from '@/components/RecruiterPanel';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export function BillingPage(): JSX.Element {
  const { t } = useTranslation();
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [justPurchased, setJustPurchased] = useState(false);
  const [params, setParams] = useSearchParams();

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      setBilling(await billingApi.getMe());
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    const sessionId = params.get('session_id');
    const success = params.get('checkout') === 'success';
    if (success && sessionId) {
      // Success-page fallback: confirm the session so access applies instantly
      // even if the Stripe webhook is delayed (idempotent server-side).
      setStatus('loading');
      void billingApi
        .confirmCheckout(sessionId)
        .then((s) => { setBilling(s); setJustPurchased(true); setStatus('ready'); })
        .catch(() => void refresh())
        .finally(() => {
          // Strip the one-time query params so a refresh doesn't re-confirm.
          const next = new URLSearchParams(params);
          next.delete('session_id');
          next.delete('checkout');
          setParams(next, { replace: true });
        });
      return;
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const ownedTier = billing?.ownedTier ?? null;
  const tier3Active = billing?.tier3Active ?? false;
  const ownedLabel = ownedTier === 'tier2' ? t('billing.plan.ownedTier2') : ownedTier === 'tier1' ? t('billing.plan.ownedTier1') : null;
  const sub = billing?.subscription;

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('billing.page.title')}</h1>
        <p className="muted">{t('billing.page.blurb')}</p>
      </header>

      {justPurchased && (
        <p className="muted" role="status" data-testid="purchase-confirmed">{t('billing.page.purchaseConfirmed')}</p>
      )}
      {status === 'loading' && <p className="muted" role="status">{t('common.loading')}</p>}
      {status === 'error' && <p className="error" role="alert">{t('billing.page.loadError')}</p>}

      {status === 'ready' && billing && (
        <>
          <section className="admin-card">
            <h2 className="admin-card__title">{t('billing.plan.title')}</h2>
            {billing.kind === 'comp' || billing.kind === 'admin' ? (
              <p className="muted">{t('billing.plan.comped')}</p>
            ) : (
              <>
                {ownedLabel ? (
                  <p>
                    <Trans
                      i18nKey="billing.plan.owned"
                      values={{ label: ownedLabel }}
                      components={{ strong: <strong />, muted: <span className="muted" /> }}
                    />
                  </p>
                ) : billing.dailyMedia ? (
                  <p className="muted">
                    {t('billing.plan.freePlays', { remaining: billing.dailyMedia.remaining, limit: billing.dailyMedia.limit })}
                    {billing.dailyMedia.adRequired ? t('billing.plan.adSuffix') : ''}
                  </p>
                ) : (
                  <p>{t('billing.plan.noPlan')}</p>
                )}
                {tier3Active && sub && (
                  <p>
                    <Trans
                      i18nKey="billing.plan.premiumSub"
                      values={{ status: sub.status, price: formatCents(sub.priceCents) }}
                      components={{ strong: <strong /> }}
                    />
                    {sub.currentPeriodEnd && (
                      <span className="muted">{t('billing.plan.renews', { date: new Date(sub.currentPeriodEnd).toLocaleDateString() })}</span>
                    )}
                  </p>
                )}
                <Link to="/subscribe" className="btn" style={{ display: 'inline-block', marginTop: 8 }}>
                  {ownedTier ? t('billing.plan.upgrade') : t('billing.plan.choosePlan')}
                </Link>
              </>
            )}
          </section>

          <RecruiterPanel />
        </>
      )}
    </main>
  );
}
